import { Button, Chip } from 'components/ui'
import React from 'react'
import { createPortal } from 'react-dom'
import { Controller } from 'react-hook-form'
import { useSelector } from 'react-redux'
import {
  User,
  Tag,
  Users,
  Eye,
  Folder,
  Clock,
  Flag,
  Settings,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Check,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react'
import { Summery } from '../../Steps/TaskSettingStep/TimeFramePreview/Summery'
import { isRtlSelector } from 'redux/selectors/general'
import LocaleText from 'components/LocaleText'
import UsersList from 'components/CommonComponents/UsersList'
import TagsList from 'components/CommonComponents/TagsList'
import OptimizedGroupsTree from 'components/CommonComponents/OptimizedGroupsTree'
import { SelectionMode } from 'components/CommonComponents/OptimizedGroupsTree/hooks/useSelection'
import { PRIORITY_OPTIONS } from '../../utils'
import ChooseAuditors from '../../Steps/TaskSettingStep/ChooseAuditors'
import TaskTypeSelection from '../../Steps/TaskSettingStep/TaskTypeSelection'
import PrioritySelection from '../../Steps/TaskSettingStep/PrioritySelection'
import { IndividualResultsSwitch } from '../../Steps/TaskSettingStep/IndividualResultsSwitch'
import { SchedulePushSwitch } from '../../Steps/TaskSettingStep/SchedulePushSwitch'
import { DisableGalleryUploadSwitch } from '../../Steps/TaskSettingStep/DisableGalleryUploadSwitch'
import { FeedbackLimitSwitch } from '../../Steps/TaskSettingStep/FeedbackLimitSwitch'
import { AllowCompletionAfterDeadlineSwitch } from '../../Steps/TaskSettingStep/AllowCompletionAfterDeadlineSwitch'
import { TimeFrameFields } from '../../Steps/TimeFrameSelection'
import { InlineSettingsContext } from '../../contexts/InlineSettingsContext'
import { SettingsSection } from '../../model/settings'
import { useSettingsBuilder } from '../../hooks/useSettingsBuilder'
import { useSettingsText } from './strings'
import { SettingsReadyIllustration } from '../SettingsReadyIllustration'
import styles from './styles.module.css'

interface ISettingsBuilderProps {
  headerSlot?: HTMLDivElement | null
  actionLabel?: string
  hiddenSections?: SettingsSection[]
  extension?: { label: string; summary: React.ReactNode; content: React.ReactNode }
  advancedExtra?: React.ReactNode
  advancedExtraEnabled?: boolean
  showReviewDeadline?: boolean
}
const EMPTY: string[] = []
const AUDIENCE_ICONS = { groups: Users, users: User, tags: Tag }
export const SettingsBuilder: React.FC<ISettingsBuilderProps> = ({
  headerSlot,
  actionLabel,
  hiddenSections = [],
  extension,
  advancedExtra,
  advancedExtraEnabled,
  showReviewDeadline = true,
}) => {
  const rtl = useSelector(isRtlSelector)
  const t = useSettingsText()
  const {
    isVmTask,
    sumbitStep,
    isSubmitting,
    control,
    values,
    active,
    setActive,
    audienceType,
    setAudienceType,
    editorRef,
    listHeight,
    config,
    permissions,
    views,
    allowedGroups,
    allowedUsers,
    audience,
    invalidSections,
    canPublish,
    changeAudience,
    changeWatchers,
    closeSection,
  } = useSettingsBuilder()

  const audienceTotals = {
    groups: allowedGroups.length,
    users: allowedUsers.length,
    tags: (permissions.tags || EMPTY).length,
  }

  const hasAudienceSelection = !!audience[audienceType]?.length
  const clearAudienceAction = (
    <Button
      variant="ghost"
      type="button"
      h="36px"
      minWidth="64px"
      flexShrink={0}
      visibility={hasAudienceSelection ? 'visible' : 'hidden'}
      aria-hidden={!hasAudienceSelection}
      isDisabled={!hasAudienceSelection}
      tabIndex={hasAudienceSelection ? 0 : -1}
      onClick={() => changeAudience([])}
    >
      {t.clear}
    </Button>
  )

  const userNames = (ids: string[] = []) =>
    ids
      .map((id) => {
        const user = config.retailUsersObject?.[id]

        return user ? [user.first_name, user.last_name].filter(Boolean).join(' ') : id
      })
      .slice(0, 3)
      .join(', ')

  const audienceNames = [
    ...(audience.groups || []).map((id: string) => config.groups?.[id]?.name || id),
    ...userNames(audience.users).split(', ').filter(Boolean),
    ...(audience.tags || []).map((id: string) => config.tags?.find((tag) => tag.sk === id)?.name || id),
  ]

  const audienceCount = ['groups', 'users', 'tags'].reduce((count, key) => count + (audience[key]?.length || 0), 0)

  const priority = PRIORITY_OPTIONS.find((item) => item.value === values.priority)
  const folder = views.find((item) => item.task_view_id === values.view)
  const advancedCount = [
    advancedExtraEnabled,
    values.is_individual_results,
    values.send_schedule_push,
    values.disable_gallery_upload,
    values.allow_completion_after_deadline,
    ...(isVmTask ? [values.rework_limit != null] : []),
  ].filter(Boolean).length
  const sections: { id: SettingsSection; icon: typeof Users; summary: React.ReactNode; required?: boolean }[] = [
    {
      id: 'audience',
      icon: Users,
      required: true,
      summary: audienceCount ? `${audienceCount} ${t.selected} · ${audienceNames.slice(0, 3).join(', ')}` : t.missing,
    },
    ...(isVmTask
      ? [
          {
            id: 'auditors' as SettingsSection,
            icon: ShieldCheck,
            required: true,
            summary: values.auditors?.length
              ? `${values.auditors.length} ${t.selected} · ${userNames(values.auditors)}`
              : t.missing,
          },
        ]
      : []),
    {
      id: 'watchers',
      icon: Eye,
      summary: values.watchers?.length
        ? `${values.watchers.length} ${t.selected} · ${userNames(values.watchers)}`
        : t.empty,
    },
    {
      id: 'view',
      icon: Folder,
      summary: folder ? <LocaleText text={folder.literal || folder.task_view_name} /> : t.missing,
    },
    {
      id: 'schedule',
      icon: Clock,
      summary: <Summery timeFrame={values} />,
    },
    { id: 'priority', icon: Flag, summary: priority && <LocaleText text={priority.label} /> },
    ...(extension ? [{ id: 'extension' as SettingsSection, icon: Clock, summary: extension.summary }] : []),
    {
      id: 'advanced',
      icon: Settings,
      summary: `${advancedCount} / ${(isVmTask ? 5 : 4) + (advancedExtra ? 1 : 0)} ${t.enabled}`,
    },
  ]
  const visibleSections = sections.filter((section) => !hiddenSections.includes(section.id))
  const sectionLabel = (id: SettingsSection) => (id === 'extension' ? extension?.label : t[id])
  const current = visibleSections.find((section) => section.id === active)
  const CurrentIcon = current?.icon || Settings

  const navigate = (direction: number) => {
    const index = visibleSections.findIndex((section) => section.id === active)

    closeSection(visibleSections[index + direction]?.id || null)
  }

  const renderPublishButton = (withArrow = false) => (
    <Button
      px={withArrow ? '28px' : '24px'}
      py={withArrow ? '14px' : '10px'}
      fontSize={withArrow ? '16px' : undefined}
      minWidth="105px"
      borderRadius={withArrow ? '12px' : '9px'}
      bg="#222222"
      fontWeight={600}
      _hover={{ bg: '#333333' }}
      _disabled={{ bg: '#a8abad', opacity: 1, cursor: 'not-allowed', transform: 'none' }}
      variant="primary"
      height="auto"
      type="button"
      isDisabled={!canPublish}
      onClick={sumbitStep}
    >
      {isSubmitting ? t.busy : actionLabel || t.publish}
      {withArrow &&
        (rtl ? (
          <ArrowLeft size={18} aria-hidden="true" style={{ marginInlineStart: 12 }} />
        ) : (
          <ArrowRight size={18} aria-hidden="true" style={{ marginInlineStart: 12 }} />
        ))}
    </Button>
  )
  const publish = <div className={styles.publish}>{renderPublishButton()}</div>

  return (
    <InlineSettingsContext.Provider value={true}>
      {headerSlot ? createPortal(publish, headerSlot) : publish}
      <div
        className={`${styles.builder} ${active ? styles.hasEditor : ''}`}
        dir={rtl ? 'rtl' : 'ltr'}
        data-settings-workspace
        data-settings-editing={!!active}
      >
        <section className={styles.listPanel}>
          <header>
            <strong>{t.settings}</strong>
            {invalidSections.length > 0 && (
              <Chip
                label={
                  <>
                    {invalidSections.length} {t.remaining}
                  </>
                }
                size="sm"
                radius="5px"
                color="#bd4545"
                bg="#fdeaea"
                border="none"
                style={{ marginInlineStart: 'auto' }}
              />
            )}
          </header>
          <div className={styles.cardList}>
            {visibleSections.map(({ id, icon: Icon, summary, required }) => (
              <button
                type="button"
                key={id}
                className={`${styles.card} ${active === id ? styles.isSelected : ''} ${
                  invalidSections.includes(id) ? styles.isMissing : ''
                }`}
                onClick={() => setActive(id)}
                aria-pressed={active === id}
                data-settings-section={id}
                data-settings-missing={invalidSections.includes(id)}
              >
                <span className={`${styles.icon} ${styles[id] || ''}`}>
                  <Icon size={19} />
                </span>
                <span className={styles.cardCopy}>
                  <strong>
                    {sectionLabel(id)} {required && <em>*</em>}{' '}
                    <Chip
                      label={<>{invalidSections.includes(id) ? t.required : t.set}</>}
                      size="sm"
                      radius="5px"
                      color={invalidSections.includes(id) ? '#bd4545' : '#68883c'}
                      bg={invalidSections.includes(id) ? '#fdeaea' : '#eaf3e6'}
                      border="none"
                    />
                  </strong>
                  <small>{summary}</small>
                </span>
                {rtl ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
              </button>
            ))}
          </div>
        </section>
        <section className={styles.editorPanel}>
          <header>
            <Button
              variant="icon"
              height="auto"
              className={styles.mobileBack}
              type="button"
              aria-label={t.back}
              onClick={() => setActive(null)}
            >
              {rtl ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </Button>
            <span className={`${styles.icon} ${(active && styles[active]) || ''}`}>
              <CurrentIcon size={19} />
            </span>
            <strong>{current ? sectionLabel(current.id) : t.settings}</strong>
            {current?.required && (
              <Chip
                label={<>{t.required}</>}
                size="sm"
                radius="5px"
                color="#bd4545"
                bg="#fdeaea"
                border="none"
                style={{ marginInlineStart: 'auto' }}
              />
            )}
          </header>
          <div className={styles.editorScroll} ref={editorRef}>
            {!active && (
              <div className={styles.readyPanel}>
                {invalidSections.length ? <Check size={28} /> : <SettingsReadyIllustration />}
                <h3>{invalidSections.length ? t.settings : actionLabel ? t.ready : t.readyToPublish}</h3>
                <p>{invalidSections.length || actionLabel ? t.choose : t.publishHint}</p>
                {!invalidSections.length && <div className={styles.readyAction}>{renderPublishButton(true)}</div>}
                {invalidSections.length > 0 && (
                  <Button
                    px="18px"
                    py="9px"
                    borderWidth="1px"
                    borderRadius="9px"
                    borderColor="#dfe3e6"
                    variant="secondary"
                    height="auto"
                    type="button"
                    onClick={() => setActive(invalidSections[0])}
                  >
                    {sectionLabel(invalidSections[0])}
                  </Button>
                )}
              </div>
            )}
            {/* Keep fields mounted: RHF v5 unregisters values and validators on unmount. */}
            <div hidden={active !== 'audience'}>
              <Controller
                name="audience"
                control={control}
                rules={{
                  validate: (value) => ['groups', 'users', 'tags'].some((key) => value?.[key]?.length) || t.missing,
                }}
                as={<span hidden />}
              />
              <div className={styles.audienceTypes}>
                {(['groups', 'users', 'tags'] as const).map((type) => {
                  const Icon = AUDIENCE_ICONS[type]

                  return (
                    <button
                      type="button"
                      className={type === audienceType ? styles.isSelected : ''}
                      aria-pressed={type === audienceType}
                      key={type}
                      onClick={() => setAudienceType(type)}
                    >
                      <span className={styles.audienceTypeHeading}>
                        <span className={styles.audienceTypeLabel}>
                          <Icon size={18} aria-hidden="true" />
                          {t[type]}
                        </span>
                        {!!audience[type]?.length && (
                          <Chip
                            label={<>{t.set}</>}
                            size="sm"
                            radius="5px"
                            color="#68883c"
                            bg="#eaf3e6"
                            border="none"
                          />
                        )}
                      </span>
                      <small>
                        {audience[type]?.length || 0} / {audienceTotals[type]} {t.selected}
                      </small>
                    </button>
                  )
                })}
              </div>
              {active === 'audience' &&
                (audienceType === 'groups' ? (
                  <OptimizedGroupsTree
                    searchWidth="50%"
                    searchActions={clearAudienceAction}
                    isCloseMode
                    selectSearchResult
                    groups={permissions.groups || {}}
                    allowedGroups={allowedGroups}
                    selectionMode={SelectionMode.MULTIPLE}
                    initialSelectedIds={audience.groups || EMPTY}
                    onSelectionChange={changeAudience}
                    listHeight={listHeight}
                  />
                ) : audienceType === 'users' ? (
                  <UsersList
                    selectionSections={{ selected: t.selected, unselected: t.unselected }}
                    searchWidth="50%"
                    searchActions={clearAudienceAction}
                    allowedUserIds={allowedUsers}
                    initialSelectedIds={audience.users || EMPTY}
                    onSelectionChange={changeAudience}
                    listHeight={listHeight}
                  />
                ) : (
                  <TagsList
                    selectionSections={{ selected: t.selected, unselected: t.unselected }}
                    searchWidth="50%"
                    searchActions={clearAudienceAction}
                    allowedTagIds={permissions.tags || EMPTY}
                    initialSelectedIds={audience.tags || EMPTY}
                    onSelectionChange={changeAudience}
                    listHeight={listHeight}
                  />
                ))}
            </div>
            {isVmTask && (
              <div hidden={active !== 'auditors'}>
                <ChooseAuditors inline listHeight={listHeight} />
              </div>
            )}
            <div hidden={active !== 'watchers'}>
              <Controller name="watchers" control={control} as={<span hidden />} />
              {active === 'watchers' && (
                <UsersList
                  initialSelectedIds={values.watchers || EMPTY}
                  onSelectionChange={changeWatchers}
                  listHeight={listHeight}
                />
              )}
            </div>
            <div hidden={active !== 'view'}>
              {hiddenSections.includes('view') ? (
                <Controller name="view" control={control} as={<span hidden />} />
              ) : (
                <TaskTypeSelection inline />
              )}
            </div>
            <div hidden={active !== 'schedule'} className={styles.schedule}>
              <TimeFrameFields showReviewDeadline={showReviewDeadline} />
            </div>
            <div hidden={active !== 'priority'}>
              <PrioritySelection inline />
            </div>
            <div hidden={active !== 'advanced'} className={styles.advanced}>
              <IndividualResultsSwitch number={1} />
              <SchedulePushSwitch number={2} />
              <DisableGalleryUploadSwitch number={3} />
              {isVmTask && <FeedbackLimitSwitch number={4} />}
              <AllowCompletionAfterDeadlineSwitch number={isVmTask ? 5 : 4} />
              {advancedExtra}
            </div>
            {extension && <div hidden={active !== 'extension'}>{extension.content}</div>}
          </div>
          {active && (
            <footer>
              <small>{t.changes}</small>
              <Button
                px="18px"
                py="9px"
                borderWidth="1px"
                borderRadius="9px"
                borderColor="#dfe3e6"
                variant="secondary"
                height="auto"
                type="button"
                className={styles.stepNavigation}
                isDisabled={visibleSections[0].id === active}
                onClick={() => navigate(-1)}
              >
                {t.previous}
              </Button>
              <Button
                px="18px"
                py="9px"
                borderWidth="1px"
                borderRadius="9px"
                borderColor="#dfe3e6"
                variant="secondary"
                height="auto"
                type="button"
                className={styles.stepNavigation}
                onClick={() => navigate(1)}
              >
                {t.next}
              </Button>
              <Button
                px="18px"
                py="9px"
                borderWidth="1px"
                borderRadius="9px"
                borderColor="#343a3e"
                variant="secondary"
                height="auto"
                type="button"
                onClick={() => closeSection()}
              >
                {t.done}
              </Button>
            </footer>
          )}
        </section>
      </div>
    </InlineSettingsContext.Provider>
  )
}
